import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { keccak256, parseEther, toUtf8Bytes } from 'ethers';
import { toast } from 'react-toastify';
import { Plus, ExternalLink } from 'lucide-react';
import { AppShell } from '../components/AppShell';
import { WalletConnector } from '../components/data-exchange/WalletConnector';
import { DataMarketplace } from '../components/data-exchange/DataMarketplace';
import { PublishOfferForm } from '../components/data-exchange/PublishOfferForm';
import { PoEPanel } from '../components/data-exchange/PoEPanel';
import { MDNStatusPanel } from '../components/data-exchange/MDNStatusPanel';
import { TxHistory } from '../components/data-exchange/TxHistory';
import { AgreementPanel } from '../components/data-exchange/AgreementPanel';
import { DataAssetPanel } from '../components/data-exchange/DataAssetPanel';
import { AppButton } from '../components/ui/AppButton';
import { PageContainer } from '../components/ui/PageContainer';
import { PageHeader } from '../components/ui/PageHeader';
import { StatCard } from '../components/ui/StatCard';
import { SegmentedTabs } from '../components/ui/SegmentedTabs';
import { useWallet } from '../hooks/useWallet';
import { blockchainAPI, dataExchangeAPI } from '../services/api';
import {
  acceptOfferOnChain,
  createOfferOnChain,
  fetchOnChainOffers,
  notarizeHashOnChain,
  verifyHashOnChain,
} from '../services/blockchainClient';

const MDN_EVENTS_POLL_INTERVAL_MS = 15000;

const TABS = [
  { id: 'browse',        label: 'Browse'        },
  { id: 'my-assets',     label: 'My Assets'     },
  { id: 'agreements',    label: 'Agreements'    },
  { id: 'notarization',  label: 'Notarization'  },
];

/* ── Inline helpers ──────────────────────────────────────────── */
const sanitizeHashReference = (value) =>
  String(value || '').replace(/[^\w:/#?&=.-]/g, '').trim();

const normalizeWalletAddress = (value) => String(value || '').trim().toLowerCase();

const truncateAddress = (value) => (value ? `${value.slice(0, 6)}...${value.slice(-4)}` : '');

const buildExplorerLink = (txHash) => {
  const base = process.env.REACT_APP_BLOCK_EXPLORER_BASE_URL || '';
  if (!base || !txHash) return '';
  return `${base.replace(/\/$/, '')}/tx/${txHash}`;
};

const refreshNotifications = () => {
  window.dispatchEvent(new Event('notifications:refresh'));
};

const isUserRejectedError = (error) => {
  const code = error?.code ?? error?.error?.code ?? error?.info?.error?.code;
  const message = String(error?.message || '').toLowerCase();
  return (
    code === 4001 ||
    code === 'ACTION_REJECTED' ||
    message.includes('user denied') ||
    message.includes('action_rejected')
  );
};

const getShortTxErrorMessage = (error, fallbackMessage) => {
  const message = String(
    error?.response?.data?.error ||
    error?.shortMessage ||
    error?.reason ||
    error?.message ||
    fallbackMessage
  );
  const lower = message.toLowerCase();
  if (isUserRejectedError(error)) return 'Transaction was rejected by the wallet';
  if (lower.includes('insufficient funds')) return 'Insufficient funds';
  if (lower.includes('out of gas') || lower.includes('intrinsic gas')) return 'Gas too low';
  if (lower.includes('timeout') || lower.includes('timed out')) return 'Confirmation timed out';
  if (lower.includes('revert') || lower.includes('call exception')) return 'Reverted by contract';
  return message;
};

const notifyTxError = (error, fallbackMessage) => {
  if (isUserRejectedError(error)) {
    toast.info('Transaction canceled in MetaMask.');
    return;
  }
  toast.error(`Transaction failed: ${getShortTxErrorMessage(error, fallbackMessage)}`);
};

/**
 * Data Exchange page with tab navigation:
 * Marketplace | Agreements | Data Assets | Notarization
 */
export const DataExchange = () => {
  const { address, provider, signer, isConnected, isNetworkMismatch, refreshWalletState } = useWallet();

  const [activeTab, setActiveTab] = useState(TABS[0].id);
  const [showPublishForm, setShowPublishForm] = useState(false);

  const [offers, setOffers] = useState([]);
  const [declinedOfferIds, setDeclinedOfferIds] = useState([]);
  const [loadingOffers, setLoadingOffers] = useState(false);
  const [acceptingOfferId, setAcceptingOfferId] = useState(null);
  const [publishingOffer, setPublishingOffer] = useState(false);
  const [notarizing, setNotarizing] = useState(false);
  const [mdnEvents, setMdnEvents] = useState([]);
  const [loadingMdnEvents, setLoadingMdnEvents] = useState(false);
  const [transactions, setTransactions] = useState([]);
  const [loadingTransactions, setLoadingTransactions] = useState(false);
  const declinedStorageKey = useMemo(
    () => `data-exchange-declined-offers:${String(address || 'guest').toLowerCase()}`,
    [address]
  );

  const loadOffers = useCallback(async () => {
    setLoadingOffers(true);
    try {
      const dbPromise = dataExchangeAPI.listOffers();
      const chainPromise =
        provider && !isNetworkMismatch
          ? fetchOnChainOffers(provider)
          : blockchainAPI.getOnChainOffers();

      const [dbOffers, chainOffers] = await Promise.all([dbPromise, chainPromise]);
      const dbByOfferId = new Map((dbOffers || []).map((o) => [String(o.offer_id ?? ''), o]));
      // getActiveOffers() already filters expired/cancelled offers on-chain, so
      // chainOffers contains only live offers. We merge DB metadata on top but
      // always keep the on-chain metadata (country, typology) as the source of truth.
      const merged = (chainOffers || []).map((chainOffer) => {
        const dbMeta = dbByOfferId.get(String(chainOffer.id)) || {};
        return {
          ...dbMeta,
          ...chainOffer,
          onChainOfferId: Number(chainOffer.id),
          dbOfferId: dbMeta.id || null,
          providerDisplay: truncateAddress(chainOffer.provider),
          // Merge DB metadata (may have poeHash, poeTxHash) under chain metadata
          // (country, typology) so on-chain values always win for filtering.
          metadata: {
            ...(dbMeta.metadata || {}),
            ...chainOffer.metadata,
          },
        };
      });
      setOffers(merged);
    } catch (error) {
      toast.error(error.message || 'Failed to load offers');
    } finally {
      setLoadingOffers(false);
    }
  }, [provider, isNetworkMismatch]);

  const loadTransactions = useCallback(async () => {
    if (!address) return;
    setLoadingTransactions(true);
    try {
      const rows = await dataExchangeAPI.listTransactions(address);
      setTransactions(rows || []);
    } catch (error) {
      toast.error(error.message || 'Failed to load transaction history');
    } finally {
      setLoadingTransactions(false);
    }
  }, [address]);

  const loadMdnEvents = useCallback(async () => {
    setLoadingMdnEvents(true);
    try {
      const rows = await blockchainAPI.getNotarizationEvents(10);
      setMdnEvents(
        (rows || []).map((event) => ({ ...event, explorerLink: buildExplorerLink(event.transactionHash) }))
      );
    } catch (error) {
      toast.error(error.message || 'Failed to load MDN events');
    } finally {
      setLoadingMdnEvents(false);
    }
  }, []);

  useEffect(() => {
    loadOffers();
    loadMdnEvents();
  }, [loadOffers, loadMdnEvents]);

  useEffect(() => {
    loadTransactions();
  }, [loadTransactions]);

  useEffect(() => {
    try {
      const stored = JSON.parse(localStorage.getItem(declinedStorageKey) || '[]');
      if (Array.isArray(stored)) {
        setDeclinedOfferIds(stored.map((id) => String(id)));
      } else {
        setDeclinedOfferIds([]);
      }
    } catch {
      setDeclinedOfferIds([]);
    }
  }, [declinedStorageKey]);

  useEffect(() => {
    const intervalId = window.setInterval(() => {
      loadMdnEvents();
    }, MDN_EVENTS_POLL_INTERVAL_MS);

    return () => {
      window.clearInterval(intervalId);
    };
  }, [loadMdnEvents]);

  const handleAcceptOffer = async (offer) => {
    if (!signer || !isConnected) { toast.error('Connect wallet before accepting offers'); return; }
    if (isNetworkMismatch) { toast.error('Switch to expected chain before accepting offers'); return; }
    if (normalizeWalletAddress(offer.provider) === normalizeWalletAddress(address)) {
      toast.error('You cannot accept your own offer. Switch to a different wallet to buy it.');
      return;
    }
    const onChainOfferId = Number(offer.onChainOfferId ?? offer.id);
    if (!Number.isFinite(onChainOfferId)) {
      toast.error('Offer is missing a valid on-chain ID');
      return;
    }
    setAcceptingOfferId(onChainOfferId);
    try {
      toast.info('Transaction submitted: Please confirm the offer acceptance in your wallet.');
      const tx = await acceptOfferOnChain({ signer, offerId: onChainOfferId, priceWei: offer.priceWei });
      toast.success(`Smart contract accepted: Offer #${tx.offerId ?? onChainOfferId} was confirmed.`);
      if (offer.dbOfferId) {
        try {
          await dataExchangeAPI.markOfferAccepted(tx.offerId ?? onChainOfferId, { transactionHash: tx.txHash });
        } catch (dbError) {
          console.warn('Offer accepted on-chain but DB status sync failed:', dbError);
        }
      } else {
        console.warn('Offer accepted on-chain but no DB metadata row exists; skipping DB status sync.');
      }
      await dataExchangeAPI.saveTransaction({
        walletAddress: address,
        transactionType: 'offer_accepted',
        counterparty: offer.provider,
        amountWei: offer.priceWei,
        status: 'confirmed',
        transactionHash: tx.txHash,
        explorerLink: buildExplorerLink(tx.txHash),
        details: { offerId: tx.offerId ?? onChainOfferId, dataType: offer.dataType, dbOfferId: offer.dbOfferId || null },
      });
      refreshNotifications();
      // Re-fetch the wallet's ETH balance so the displayed balance reflects the
      // payment. MetaMask does not push balance updates automatically — we must
      // pull them after the transaction is confirmed.
      await Promise.all([loadOffers(), loadTransactions(), refreshWalletState()]);
    } catch (error) {
      if (address && !isUserRejectedError(error)) {
        try {
          const txHash = error?.transactionHash || error?.receipt?.hash || null;
          await dataExchangeAPI.saveTransaction({
            walletAddress: address,
            transactionType: 'offer_accepted',
            counterparty: offer.provider,
            amountWei: offer.priceWei,
            status: 'failed',
            transactionHash: txHash,
            explorerLink: buildExplorerLink(txHash),
            details: {
              offerId: onChainOfferId,
              dataType: offer.dataType,
              dbOfferId: offer.dbOfferId || null,
              errorMessage: getShortTxErrorMessage(error, 'Failed to accept offer'),
            },
          });
          refreshNotifications();
        } catch (historyError) {
          console.warn('Failed to record failed offer acceptance:', historyError);
        }
      }
      notifyTxError(error, 'Failed to accept offer');
    } finally {
      setAcceptingOfferId(null);
    }
  };

  const handleDeclineOffer = async (offer) => {
    const onChainOfferId = String(offer.onChainOfferId ?? offer.id ?? '');
    if (!onChainOfferId) {
      toast.error('Offer is missing a valid on-chain ID');
      return;
    }

    setDeclinedOfferIds((current) => {
      const next = current.includes(onChainOfferId) ? current : [...current, onChainOfferId];
      localStorage.setItem(declinedStorageKey, JSON.stringify(next));
      return next;
    });

    toast.info('Offer declined and hidden from your marketplace view.');

    if (!address) return;

    try {
      await dataExchangeAPI.saveTransaction({
        walletAddress: address,
        transactionType: 'offer_declined',
        counterparty: offer.provider,
        amountWei: offer.priceWei || '0',
        status: 'declined',
        transactionHash: null,
        explorerLink: '',
        details: { offerId: Number(onChainOfferId), dataType: offer.dataType, dbOfferId: offer.dbOfferId || null },
      });
      refreshNotifications();
      await loadTransactions();
    } catch (error) {
      notifyTxError(error, 'Failed to record declined offer');
    }
  };

  const handlePublishOffer = async (values) => {
    if (!signer || !isConnected) { toast.error('Connect wallet before publishing offers'); return; }
    if (isNetworkMismatch) { toast.error('Switch to expected chain before publishing offers'); return; }
    setPublishingOffer(true);
    try {
      const sanitizedRef = sanitizeHashReference(values.dataHashRef);
      const proofHash = keccak256(toUtf8Bytes(sanitizedRef));
      toast.info('Transaction submitted: Please confirm the data proof in your wallet.');
      const poeTx = await notarizeHashOnChain({ signer, hash: proofHash });
      if (poeTx.alreadyNotarized) toast.info('PoE already notarized — reusing existing proof');
      else if (poeTx.txHash) toast.success('Transaction confirmed: Data proof notarized.');
      const validUntilUnix = Math.floor(Date.now() / 1000) + Number(values.validityDays) * 86400;
      toast.info('Deploying contract: Please confirm the offer transaction and wait for confirmation.');
      const offerTx = await createOfferOnChain({
        signer,
        dataType: values.dataType,
        ipfsCid: sanitizedRef,
        priceEth: values.priceEth,
        validUntilUnix,
        country: values.country || '',
        buildingTypology: values.typology || '',
        description: values.dataDescription || '',
        buildingId: values.buildingReference || '',
      });
      if (offerTx.txHash) toast.success('Smart contract deployed: Offer published on-chain.');

      // Refresh the marketplace immediately after on-chain success so the offer
      // appears even if the backend metadata save fails below.
      let latestOffers = [];
      try {
        latestOffers = await fetchOnChainOffers(provider || signer.provider);
        setOffers((prev) => {
          // Build a merged list: keep existing offers that are not in the new
          // on-chain result, then append all on-chain offers with their DB meta.
          const dbByOfferId = new Map((prev).map((o) => [String(o.onChainOfferId ?? ''), o]));
          return latestOffers.map((chainOffer) => {
            const existing = dbByOfferId.get(String(chainOffer.id)) || {};
            return {
              ...existing,
              ...chainOffer,
              onChainOfferId: Number(chainOffer.id),
              dbOfferId: existing.dbOfferId || null,
              providerDisplay: truncateAddress(chainOffer.provider),
              metadata: { ...(existing.metadata || {}), ...chainOffer.metadata },
            };
          });
        });
      } catch {
        // If we can't fetch chain offers here, the full loadOffers() below will retry.
      }

      // DB metadata is supplementary (stores poeHash, poeTxHash). Failures here
      // must NOT hide the on-chain offer or prevent a full refresh.
      try {
        await dataExchangeAPI.saveOfferMetadata({
          offerId: offerTx.offerId,
          dataType: values.dataType,
          buildingReference: values.buildingReference,
          dataDescription: values.dataDescription,
          priceWei: parseEther(String(values.priceEth)).toString(),
          currency: 'ETH',
          validUntil: new Date(validUntilUnix * 1000).toISOString(),
          dataHash: sanitizedRef,
          providerAddress: address,
          transactionHash: offerTx.txHash,
          metadata: {
            country: values.country || '',
            typology: values.typology || '',
            poeHash: proofHash,
            poeTxHash: poeTx.txHash,
          },
        });
        toast.success(`Smart contract pending: Offer #${offerTx.offerId} is live and pending acceptance.`);
        refreshNotifications();
        await dataExchangeAPI.saveTransaction({
          walletAddress: address,
          transactionType: 'offer_created',
          counterparty: null,
          amountWei: parseEther(String(values.priceEth)).toString(),
          status: 'confirmed',
          transactionHash: offerTx.txHash,
          explorerLink: buildExplorerLink(offerTx.txHash),
          details: {
            dataType: values.dataType,
            buildingReference: values.buildingReference,
            offerPriceWei: parseEther(String(values.priceEth)).toString(),
            poeHash: proofHash,
            poeTxHash: poeTx.txHash,
          },
        });
      } catch (dbError) {
        console.warn('Offer is on-chain but metadata could not be saved to DB:', dbError);
      }

      // Final full refresh — reconciles chain + DB metadata and updates tx history.
      await Promise.all([loadOffers(), loadTransactions()]);
    } catch (error) {
      if (address && !isUserRejectedError(error)) {
        try {
          const txHash = error?.transactionHash || error?.receipt?.hash || null;
          await dataExchangeAPI.saveTransaction({
            walletAddress: address,
            transactionType: 'offer_created',
            counterparty: null,
            amountWei: values.priceEth ? parseEther(String(values.priceEth)).toString() : '0',
            status: 'failed',
            transactionHash: txHash,
            explorerLink: buildExplorerLink(txHash),
            details: {
              dataType: values.dataType,
              buildingReference: values.buildingReference,
              errorMessage: getShortTxErrorMessage(error, 'Failed to publish offer'),
            },
          });
          refreshNotifications();
        } catch (historyError) {
          console.warn('Failed to record failed offer publication:', historyError);
        }
      }
      notifyTxError(error, 'Failed to publish offer');
      // Re-throw only for on-chain failures (before the offer was created).
      // DB-only failures are already swallowed above and the offer is visible.
      throw error;
    } finally {
      setPublishingOffer(false);
    }
  };

  const handleNotarizeHash = async (hash) => {
    if (!signer || !isConnected) { toast.error('Connect wallet first'); return; }
    setNotarizing(true);
    try {
      toast.info('Transaction submitted: Please confirm the notarization in your wallet.');
      const tx = await notarizeHashOnChain({ signer, hash });
      await dataExchangeAPI.saveTransaction({
        walletAddress: address,
        transactionType: 'poe_notarize',
        counterparty: null,
        amountWei: '0',
        status: 'confirmed',
        transactionHash: tx.txHash,
        explorerLink: buildExplorerLink(tx.txHash),
        details: { hash },
      });
      toast.success('Transaction confirmed: Hash notarized successfully.');
      refreshNotifications();
      await loadTransactions();
      return tx;
    } catch (error) {
      if (address && !isUserRejectedError(error)) {
        try {
          const txHash = error?.transactionHash || error?.receipt?.hash || null;
          await dataExchangeAPI.saveTransaction({
            walletAddress: address,
            transactionType: 'poe_notarize',
            counterparty: null,
            amountWei: '0',
            status: 'failed',
            transactionHash: txHash,
            explorerLink: buildExplorerLink(txHash),
            details: {
              hash,
              errorMessage: getShortTxErrorMessage(error, 'Failed to notarize hash'),
            },
          });
          refreshNotifications();
        } catch (historyError) {
          console.warn('Failed to record failed notarization:', historyError);
        }
      }
      notifyTxError(error, 'Failed to notarize hash');
      throw error;
    } finally {
      setNotarizing(false);
    }
  };

  const handleVerifyHash = async (hash) => {
    try {
      return await verifyHashOnChain({ provider: provider || signer?.provider, hash });
    } catch (error) {
      notifyTxError(error, 'Failed to verify hash');
      throw error;
    }
  };

  const handleMdnSubscription = async (payload) => {
    try {
      await blockchainAPI.subscribeNotarization(payload);
      toast.success('MDN subscription updated');
    } catch (error) {
      notifyTxError(error, 'Failed to update MDN subscription');
    }
  };

  const summary = useMemo(
    () => ({
      openOffers: offers.length,
      latestMdnEvents: mdnEvents.length,
      transactions: transactions.length,
    }),
    [offers.length, mdnEvents.length, transactions.length]
  );

  const visibleOffers = useMemo(
    () =>
      offers.filter(
        (offer) => !declinedOfferIds.includes(String(offer.onChainOfferId ?? offer.id))
      ),
    [offers, declinedOfferIds]
  );

  const explorerBase = process.env.REACT_APP_BLOCK_EXPLORER_BASE_URL || '';

  return (
    <AppShell title="Data Exchange">
      <PageContainer>
        {/* ── Page header ──────────────────────────────────────── */}
        <PageHeader
          title="Data Exchange"
          subtitle="Discover, buy, and publish verified building data on the LiveBetter chain."
          actions={
            <>
              {explorerBase && (
                <AppButton
                  as="a"
                  variant="ghost"
                  href={explorerBase}
                  target="_blank"
                  rel="noopener noreferrer"
                  icon={<ExternalLink size={16} />}
                >
                  Explorer
                </AppButton>
              )}
              <AppButton
                variant="primary"
                icon={<Plus size={16} />}
                onClick={() => {
                  setActiveTab('browse');
                  setShowPublishForm(true);
                }}
              >
                Publish asset
              </AppButton>
            </>
          }
        />

        {/* ── Market snapshot strip ────────────────────────────── */}
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
          <StatCard
            label="Assets listed"
            value={String(summary.openOffers ?? 0)}
            sub="Currently available on-chain"
            intent="success"
          />
          <StatCard
            label="Active agreements"
            value={String(summary.transactions ?? 0)}
            sub="Across your wallet"
            intent="success"
          />
          <StatCard
            label="MDN events"
            value={String(summary.latestMdnEvents ?? 0)}
            sub="Latest notarizations"
            intent="info"
          />
          <StatCard
            label="Verified publishers"
            value={String(
              new Set(
                offers
                  .map((o) => normalizeWalletAddress(o.provider))
                  .filter(Boolean)
              ).size
            )}
            sub="Distinct on-chain providers"
            intent="success"
          />
        </div>

        {/* ── Wallet connector ─────────────────────────────────── */}
        <WalletConnector />

        {/* ── Tab bar ──────────────────────────────────────────── */}
        <SegmentedTabs
          ariaLabel="Data Exchange sections"
          tabs={TABS}
          active={activeTab}
          onChange={setActiveTab}
        />

        {/* ── Browse ───────────────────────────────────────────── */}
        {activeTab === 'browse' && (
          <>
            {showPublishForm && (
              <PublishOfferForm
                onSubmit={handlePublishOffer}
                submitting={publishingOffer}
              />
            )}
            <DataMarketplace
              offers={visibleOffers}
              loading={loadingOffers}
              onRefresh={loadOffers}
              onAcceptOffer={handleAcceptOffer}
              onDeclineOffer={handleDeclineOffer}
              acceptingOfferId={acceptingOfferId}
              walletAddress={address}
            />
          </>
        )}

        {/* ── My Assets ────────────────────────────────────────── */}
        {activeTab === 'my-assets' && <DataAssetPanel />}

        {/* ── Agreements ───────────────────────────────────────── */}
        {activeTab === 'agreements' && <AgreementPanel />}

        {/* ── Transactions ─────────────────────────────────────── */}
        {/* ── Notarization ─────────────────────────────────────── */}
        {activeTab === 'notarization' && (
          <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
            <PoEPanel
              onNotarizeHash={handleNotarizeHash}
              onVerifyHash={handleVerifyHash}
              notarizing={notarizing}
            />
            <MDNStatusPanel
              events={mdnEvents}
              loading={loadingMdnEvents}
              onRefresh={loadMdnEvents}
              onToggleSubscription={handleMdnSubscription}
            />
          </div>
        )}

        <TxHistory transactions={transactions} loading={loadingTransactions} />
      </PageContainer>
    </AppShell>
  );
};

